import { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import Papa from 'papaparse';
import { UploadCloud, FileText, X } from 'lucide-react';
import styled from '@emotion/styled';
import {
  PageHeader, PageTitle, Card, Table, Thead, Tbody,
  PrimaryButton, SecondaryButton, Badge, AlertBanner, Mono,
} from '../styled-components';
import { showToast } from '../hooks';
import { useFetchData } from '../hooks';
import { getCSRFToken, CSRF_HEADER_NAME } from '../../common/utils';

const PHONE_COLUMN = 'phone_number';
const MAX_CONTACTS = 10_000;
const MAX_PREVIEW_ROWS = 100;

// Header aliases for the phone column, in priority order. Compared after
// normalizing (lowercase, strip everything but a-z0-9), so "Phone Number",
// "phone-number", and "PhoneNumber" all collapse to "phonenumber".
const PHONE_HEADER_ALIASES = [
  'phonenumber', 'phonenumbers', 'phone', 'phones',
  'mobilenumber', 'mobilephone', 'mobile',
  'cellphone', 'cellnumber', 'cell',
  'telephone', 'tel', 'contactnumber', 'contactphone', 'msisdn', 'number',
];

function normalizeHeader(h: string): string {
  return h.trim().toLowerCase().replace(/[^a-z0-9]/g, '');
}

// Finds the phone column, tolerating naming variants. Returns the original
// field name (untouched) so it can index back into the parsed rows.
function findPhoneField(fields: string[]): string | undefined {
  const normalized = fields.map((f) => ({ field: f, key: normalizeHeader(f) }));
  for (const alias of PHONE_HEADER_ALIASES) {
    const match = normalized.find((n) => n.key === alias);
    if (match) return match.field;
  }
  return undefined;
}

// ---------------------------------------------------------------------------
// Row / state model
// ---------------------------------------------------------------------------

type RowStatus =
  | { kind: 'valid' }
  | { kind: 'invalid'; reason: string }
  | { kind: 'duplicate' };

interface ParsedRow {
  rowIndex: number; // 1-based data row number
  phone_number: string; // raw, trimmed
  data: Record<string, string>;
  status: RowStatus;
}

type State =
  | { step: 'empty' }
  | { step: 'parsing' }
  | { step: 'error'; message: string }
  | { step: 'preview'; fileName: string; rows: ParsedRow[]; dataColumns: string[] }
  | { step: 'uploading'; fileName: string; rows: ParsedRow[]; dataColumns: string[] };

interface Campaign { id: string; name: string }

// ---------------------------------------------------------------------------
// Client-side phone helpers (light, non-authoritative — the server normalizes
// and validates with Google's phonenumbers library and is the source of truth)
// ---------------------------------------------------------------------------

// Returns an error message, or null when the number looks valid.
function phoneError(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return 'Empty phone number';
  if (/^\+[1-9]\d{1,14}$/.test(trimmed)) return null;
  const digits = trimmed.replace(/\D/g, '');
  if (digits.length === 10) return null;
  if (digits.length === 11 && digits.startsWith('1')) return null;
  return 'Not a valid US phone number';
}

// Normalized key used only for detecting duplicates within the file.
function phoneKey(raw: string): string {
  const trimmed = raw.trim();
  if (trimmed.startsWith('+')) return trimmed.replace(/[^\d]/g, '');
  const digits = trimmed.replace(/\D/g, '');
  if (digits.length === 10) return `1${digits}`;
  return digits;
}

// Server rejection reasons are prefixed with "'phone_number': " — strip it.
function cleanReason(reason: string): string {
  return reason.replace(/^'phone_number':\s*/, '');
}

// ---------------------------------------------------------------------------
// Styled bits
// ---------------------------------------------------------------------------

const Dropzone = styled.div<{ active: boolean }>(({ active }) => ({
  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
  gap: 12, padding: '48px 24px', textAlign: 'center', cursor: 'pointer',
  border: `2px dashed ${active ? 'var(--accent-cyan)' : 'var(--bg-border)'}`,
  borderRadius: 'var(--radius-card)',
  background: active ? 'var(--accent-cyan-glow)' : 'var(--bg-surface)',
  color: active ? 'var(--accent-cyan)' : 'var(--text-secondary)',
  transition: 'border-color 150ms ease, background 150ms ease, color 150ms ease',
}));

const CheckLabel = styled.label({
  display: 'flex', alignItems: 'flex-start', gap: 8,
  fontSize: 14, color: 'var(--text-primary)', cursor: 'pointer', lineHeight: 1.4,
});

const SummaryRow = styled.div({
  display: 'flex', gap: 20, alignItems: 'center', flexWrap: 'wrap',
  fontSize: 13, color: 'var(--text-secondary)',
});

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function CampaignUploadPage() {
  const { campaignId } = useParams<{ campaignId: string }>();
  const navigate = useNavigate();
  const { data: campaign } = useFetchData<Campaign | null>(`/dashboard/api/campaigns/${campaignId}`, null);

  const [state, setState] = useState<State>({ step: 'empty' });
  const [dragActive, setDragActive] = useState(false);
  const [tosAccepted, setTosAccepted] = useState(false);
  const [allowDuplicates, setAllowDuplicates] = useState(false);
  const [banner, setBanner] = useState<string | null>(null);
  const [serverRejections, setServerRejections] = useState<Record<string, string>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  function reset() {
    setState({ step: 'empty' });
    setBanner(null);
    setServerRejections({});
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  function handleFile(file: File) {
    setBanner(null);
    setServerRejections({});
    if (!/\.csv$/i.test(file.name) && file.type !== 'text/csv') {
      setState({ step: 'error', message: 'Please upload a .csv file.' });
      return;
    }
    setState({ step: 'parsing' });
    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const fields = results.meta.fields ?? [];
        const phoneField = findPhoneField(fields);
        if (!phoneField) {
          setState({ step: 'error', message: `CSV must have a phone column (e.g. '${PHONE_COLUMN}', 'phone', or 'Phone Number').` });
          return;
        }
        const dataColumns = fields.filter((f) => f !== phoneField);
        const seen = new Set<string>();
        const rows: ParsedRow[] = results.data.map((r, i) => {
          const rawPhone = (r[phoneField] ?? '').trim();
          const data: Record<string, string> = {};
          for (const col of dataColumns) {
            const v = (r[col] ?? '').trim();
            if (v) data[col] = v; // skip empty cells, mirroring the CLI
          }
          const err = phoneError(rawPhone);
          let status: RowStatus;
          if (err) {
            status = { kind: 'invalid', reason: err };
          } else {
            const key = phoneKey(rawPhone);
            if (seen.has(key)) {
              status = { kind: 'duplicate' };
            } else {
              seen.add(key);
              status = { kind: 'valid' };
            }
          }
          return { rowIndex: i + 1, phone_number: rawPhone, data, status };
        });
        if (rows.length === 0) {
          setState({ step: 'error', message: 'No rows found in the file.' });
          return;
        }
        const validCount = rows.filter((r) => r.status.kind === 'valid').length;
        if (validCount > MAX_CONTACTS) {
          setState({ step: 'error', message: `Too many contacts (${validCount.toLocaleString()}). Max ${MAX_CONTACTS.toLocaleString()} per upload.` });
          return;
        }
        setState({ step: 'preview', fileName: file.name, rows, dataColumns });
      },
      error: (err) => setState({ step: 'error', message: `Failed to parse CSV: ${err.message}` }),
    });
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  }

  async function handleUpload() {
    if (state.step !== 'preview') return;
    const preview = state;
    // When "allow duplicates" is on, in-file duplicates become uploadable too;
    // otherwise only the first occurrence of each number (status 'valid') is sent.
    const uploadableRows = preview.rows.filter(
      (r) => r.status.kind === 'valid' || (allowDuplicates && r.status.kind === 'duplicate'),
    );
    if (uploadableRows.length === 0) return;

    setBanner(null);
    setServerRejections({});
    setState({ ...preview, step: 'uploading' });
    try {
      const res = await fetch(
        `/dashboard/api/campaigns/${campaignId}/contacts?allow_duplicates=${allowDuplicates}&accepted_terms_of_service=true`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', [CSRF_HEADER_NAME]: getCSRFToken() },
          body: JSON.stringify({ contacts: uploadableRows.map((r) => ({ phone_number: r.phone_number, data: r.data })) }),
        },
      );
      const body = await res.json().catch(() => ({}));
      if (res.ok) {
        showToast(`Uploaded ${(body.created ?? uploadableRows.length).toLocaleString()} contacts`, 'success');
        // Replace (not push) so the upload page isn't left in the back stack.
        navigate(`/campaigns/${campaignId}`, { replace: true });
        return;
      }
      // Upload rejected — the server insert is all-or-nothing.
      if (Array.isArray(body.rejected_entries) && body.rejected_entries.length > 0) {
        const map: Record<string, string> = {};
        for (const entry of body.rejected_entries) {
          map[entry.phone_number] = cleanReason(entry.reason);
        }
        setServerRejections(map);
        setBanner(`${body.rejected} contact(s) were rejected by the server. Fix the flagged rows (or enable "Allow duplicates") and try again.`);
      } else if (typeof body.error === 'string') {
        setBanner(body.error);
      } else if (Array.isArray(body.detail)) {
        setBanner(body.detail.map((d: { msg?: string }) => d.msg).filter(Boolean).join('; ') || `Upload failed (HTTP ${res.status})`);
      } else {
        setBanner(typeof body.detail === 'string' ? body.detail : `Upload failed (HTTP ${res.status})`);
      }
      setState(preview);
    } catch (e) {
      setBanner(e instanceof Error ? e.message : 'Upload failed');
      setState(preview);
    }
  }

  const busy = state.step === 'uploading';
  const rows = state.step === 'preview' || state.step === 'uploading' ? state.rows : [];
  const dataColumns = state.step === 'preview' || state.step === 'uploading' ? state.dataColumns : [];
  const validCount = rows.filter((r) => r.status.kind === 'valid').length;
  const invalidCount = rows.filter((r) => r.status.kind === 'invalid').length;
  const duplicateCount = rows.filter((r) => r.status.kind === 'duplicate').length;
  // What will actually be sent: valid rows, plus in-file duplicates when allowed.
  const uploadCount = validCount + (allowDuplicates ? duplicateCount : 0);

  return (
    <div>
      <PageHeader>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            onClick={() => navigate(-1)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)' }}
          >
            ←
          </button>
          <PageTitle>Add contacts{campaign ? ` to ${campaign.name}` : ''}</PageTitle>
        </div>
      </PageHeader>

      {banner && <AlertBanner variant="red">{banner}</AlertBanner>}

      {state.step === 'error' && <AlertBanner variant="red">{state.message}</AlertBanner>}

      {(state.step === 'empty' || state.step === 'parsing' || state.step === 'error') && (
        <>
          <Dropzone
            active={dragActive}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
            onDragLeave={() => setDragActive(false)}
            onDrop={onDrop}
          >
            <UploadCloud size={40} />
            <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--text-primary)' }}>
              {state.step === 'parsing' ? 'Parsing…' : 'Drag & drop a CSV, or click to choose a file'}
            </div>
            <div style={{ fontSize: 13 }}>
              The file must include a phone column (<Mono>phone_number</Mono>, <Mono>phone</Mono>, <Mono>Phone Number</Mono>, …). Any other columns are stored as contact data.
            </div>
          </Dropzone>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            style={{ display: 'none' }}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
          />
        </>
      )}

      {(state.step === 'preview' || state.step === 'uploading') && (
        <>
          <Card style={{ marginBottom: 16 }}>
            <SummaryRow>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--text-primary)' }}>
                <FileText size={15} /> {state.fileName}
              </span>
              <span><strong style={{ color: 'var(--accent-green)' }}>{validCount.toLocaleString()}</strong> valid</span>
              {invalidCount > 0 && <span><strong style={{ color: 'var(--accent-red)' }}>{invalidCount.toLocaleString()}</strong> invalid</span>}
              {duplicateCount > 0 && <span><strong style={{ color: 'var(--accent-amber)' }}>{duplicateCount.toLocaleString()}</strong> duplicate in file</span>}
              <SecondaryButton style={{ marginLeft: 'auto', padding: '5px 12px', fontSize: 13 }} onClick={reset} disabled={busy}>
                <X size={13} /> Choose different file
              </SecondaryButton>
            </SummaryRow>
          </Card>

          <Card style={{ padding: 0, overflow: 'hidden', marginBottom: 16 }}>
            <Table>
              <Thead>
                <tr>
                  <th style={{ width: 48 }}>#</th>
                  <th>Phone</th>
                  {dataColumns.map((c) => <th key={c}>{c}</th>)}
                  <th>Status</th>
                </tr>
              </Thead>
              <Tbody>
                {rows.slice(0, MAX_PREVIEW_ROWS).map((r) => {
                  const serverReason = serverRejections[r.phone_number];
                  return (
                    <tr key={r.rowIndex} style={{ cursor: 'default' }}>
                      <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{r.rowIndex}</td>
                      <td><Mono style={{ fontSize: 12 }}>{r.phone_number || '—'}</Mono></td>
                      {dataColumns.map((c) => <td key={c} style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{r.data[c] ?? ''}</td>)}
                      <td>
                        {serverReason ? (
                          <span><Badge variant="error">rejected</Badge> <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>{serverReason}</span></span>
                        ) : r.status.kind === 'valid' ? (
                          <Badge variant="active">valid</Badge>
                        ) : r.status.kind === 'duplicate' ? (
                          <Badge variant="paused">duplicate</Badge>
                        ) : (
                          <span><Badge variant="error">invalid</Badge> <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>{r.status.reason}</span></span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </Tbody>
            </Table>
            {rows.length > MAX_PREVIEW_ROWS && (
              <div style={{ padding: '10px 16px', fontSize: 12, color: 'var(--text-muted)', borderTop: '1px solid var(--bg-border)' }}>
                Showing first {MAX_PREVIEW_ROWS} of {rows.length.toLocaleString()} rows. All {validCount.toLocaleString()} valid rows will be uploaded.
              </div>
            )}
          </Card>

          <Card>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <CheckLabel>
                <input
                  type="checkbox"
                  checked={tosAccepted}
                  onChange={(e) => setTosAccepted(e.target.checked)}
                  disabled={busy}
                  style={{ accentColor: 'var(--accent-cyan)', width: 15, height: 15, marginTop: 2 }}
                />
                I confirm these contacts have consented to be contacted and that this upload complies with applicable law (including the TCPA).
              </CheckLabel>
              <CheckLabel>
                <input
                  type="checkbox"
                  checked={allowDuplicates}
                  onChange={(e) => setAllowDuplicates(e.target.checked)}
                  disabled={busy}
                  style={{ accentColor: 'var(--accent-cyan)', width: 15, height: 15, marginTop: 2 }}
                />
                Allow numbers that already exist in this campaign (skip the duplicate check).
              </CheckLabel>
              <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                <PrimaryButton onClick={handleUpload} disabled={busy || !tosAccepted || uploadCount === 0}>
                  {busy ? 'Uploading…' : `Upload ${uploadCount.toLocaleString()} contact${uploadCount === 1 ? '' : 's'}`}
                </PrimaryButton>
              </div>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
